import { wordlist } from "@scure/bip39/wordlists/english.js";

import {
  loadMockDriver,
  saveMockDriver,
} from "../../features/driver/mockIdentity";
import type { DriverProfile } from "../../types/traffic";
import type {
  GarageApi,
  GarageCustomization,
  VerifiedGaragePurchase,
} from "./GarageApi";

const PENDING_KEY = "internet-traffic-jam:mock-garage-checkout:v1";
const RESTORE_KEY = "internet-traffic-jam:mock-garage-key:v1";

function createMockGarageKey(): string {
  const values = crypto.getRandomValues(new Uint32Array(5));
  return Array.from(
    values,
    (value) => wordlist[value % wordlist.length]?.toUpperCase() ?? "TRAFFIC",
  ).join("-");
}

function applyCustomization(
  profile: DriverProfile,
  customization: GarageCustomization,
): DriverProfile {
  return {
    ...profile,
    ...customization,
    garageOwned: true,
    badge: profile.badge ?? "garage",
  };
}

export class MockGarageApi implements GarageApi {
  public createCheckout(customization: GarageCustomization): Promise<string> {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(customization));
    return Promise.resolve(
      `${location.origin}/?checkout=success&session_id=mock`,
    );
  }

  public verifyPurchase(sessionId: string): Promise<VerifiedGaragePurchase> {
    if (sessionId !== "mock") throw new Error("Mock checkout is invalid.");
    const profile = loadMockDriver();
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (!profile || !pending) throw new Error("Mock checkout expired.");
    const customization = JSON.parse(pending) as GarageCustomization;
    const updated = applyCustomization(profile, customization);
    const garageKey = createMockGarageKey();
    localStorage.setItem(RESTORE_KEY, garageKey);
    sessionStorage.removeItem(PENDING_KEY);
    saveMockDriver(updated);
    return Promise.resolve({
      garageOwned: true,
      garageKey,
      profile: updated,
    });
  }

  public restore(garageKey: string): Promise<DriverProfile> {
    const profile = loadMockDriver();
    if (
      !profile ||
      garageKey.trim().toUpperCase().replaceAll(/\s+/g, "-") !==
        localStorage.getItem(RESTORE_KEY)
    ) {
      throw new Error("Garage Pack could not be restored.");
    }
    const updated = { ...profile, garageOwned: true, badge: "garage" as const };
    saveMockDriver(updated);
    return Promise.resolve(updated);
  }

  public save(customization: GarageCustomization): Promise<DriverProfile> {
    const profile = loadMockDriver();
    if (!profile?.garageOwned) throw new Error("Garage Pack is not owned.");
    const updated = applyCustomization(profile, customization);
    saveMockDriver(updated);
    return Promise.resolve(updated);
  }

  public rotateKey(): Promise<string> {
    const key = createMockGarageKey();
    localStorage.setItem(RESTORE_KEY, key);
    return Promise.resolve(key);
  }
}
